import { APP_LOCAL_DATE_FORMAT } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoCircular } from "@/shared/model/edo-circular.model";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { ITEMS_PER_PAGE } from "@/shared/util/pagination.constants";
import { Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { Card, Button, List, ListItem, ListItemText, IconButton, Pagination } from "@/components/ui";
import { useEffect, useState } from "react";
import { IPaginationBaseState, TextFormat, Translate, translate } from "react-jhipster";
import { getEntities } from "../../shared/reducers/entities/edo-circular.reducer";
import DeleteCircular from "./components/DeleteCircular";
import EditCircular from "./components/EditCircular";

const CircularsManagement = () => {
  const dispatch = useAppDispatch();
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "id",
    order: "ASC",
  });
  const [editCircularDialog, setEditCircularDialog] = useState<{ open: boolean; entity: IEdoCircular | null }>({
    open: false,
    entity: null,
  });
  const [deleteCircularDialog, setDeleteCircularDialog] = useState<{ open: boolean; entity: IEdoCircular | null }>({
    open: false,
    entity: null,
  });
  const { updateSuccess, loading, totalItems, entities: edoCircolareList } = useAppSelector<
    EntityState<IEdoCircular>
  >((state) => state.edoCircular);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  useEffect(() => {
    getAllEntities();
  }, [
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  useEffect(() => {
    if (updateSuccess) getAllEntities();
  }, [updateSuccess]);

  const handlePagination = (currentPage: number) =>
    setPaginationState({ ...paginationState, activePage: currentPage });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({ ...paginationState, itemsPerPage: rowsPerPage, activePage: 1 });

  return (
    <>
      <Card className="surface overflow-hidden rounded-3xl p-0 text-[var(--color-mockup-text-mid)]">
        <div className="grid grid-rows-[min-content_auto_min-content]">
          <div className="border-b border-[var(--color-mockup-border)]">
            <div className="flex flex-wrap items-center justify-between gap-3 p-4">
              <h2 className="font-heading text-heading-3 font-bold text-[var(--color-mockup-text)]">
                <Translate contentKey="edocendoApp.edoCircular.home.title" />
              </h2>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <IconButton
                  onClick={getAllEntities}
                  disabled={loading}
                  className="lg:hidden"
                  aria-label="Refresh"
                >
                  <RefreshCw className="h-5 w-5" />
                </IconButton>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={getAllEntities}
                  disabled={loading}
                  className="hidden lg:inline-flex"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  <Translate contentKey="edocendoApp.edoCircular.home.refreshListLabel" />
                </Button>
                <IconButton
                  onClick={() => setEditCircularDialog({ open: true, entity: null })}
                  disabled={loading}
                  className="lg:hidden"
                  aria-label="Crea"
                >
                  <Plus className="h-5 w-5" />
                </IconButton>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={loading}
                  onClick={() => setEditCircularDialog({ open: true, entity: null })}
                  className="hidden lg:inline-flex"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  <Translate contentKey="edocendoApp.edoCircular.home.createLabel" />
                </Button>
              </div>
            </div>
          </div>

          {edoCircolareList && edoCircolareList.length > 0 ? (
            <List className="flex flex-col">
              {edoCircolareList.map((edoCircolare) => (
                <ListItem
                  key={edoCircolare.id}
                  className="flex w-full items-center gap-3 py-2.5"
                >
                  <ListItemText
                    primary={edoCircolare.title}
                    secondary={
                      <>
                        <TextFormat
                          value={edoCircolare.date}
                          type="date"
                          format={APP_LOCAL_DATE_FORMAT}
                        />
                        {" - " +
                          translate(
                            "edocendoApp.EdoCircularVisibility." + edoCircolare.visibility
                          )}
                      </>
                    }
                    className="min-w-0 flex-1"
                  />
                  <div className="flex shrink-0 items-center gap-1">
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditCircularDialog({ open: true, entity: edoCircolare })}
                      aria-label="Modifica"
                    >
                      <Pencil className="h-4 w-4" />
                    </IconButton>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteCircularDialog({ open: true, entity: edoCircolare })}
                      aria-label="Elimina"
                    >
                      <Trash2 className="h-4 w-4" />
                    </IconButton>
                  </div>
                </ListItem>
              ))}
            </List>
          ) : (
            !loading && (
              <div className="p-4 text-center">
                <span className="text-body text-[var(--color-mockup-text-mid)]">
                  <Translate contentKey="edocendoApp.edoCircular.home.notFound" />
                </span>
              </div>
            )
          )}

          <Pagination
            count={totalItems}
            page={paginationState.activePage - 1}
            rowsPerPage={paginationState.itemsPerPage}
            onPageChange={(p) => handlePagination(p + 1)}
            onRowsPerPageChange={handleRowsPerPage}
            labelRowsPerPage="Righe per pagina:"
          />
        </div>
      </Card>

      <EditCircular
        circular={editCircularDialog.entity ?? undefined}
        open={editCircularDialog.open}
        onClose={() => setEditCircularDialog({ open: false, entity: null })}
      />
      <DeleteCircular
        circular={deleteCircularDialog.entity}
        open={deleteCircularDialog.open}
        onClose={() => setDeleteCircularDialog({ open: false, entity: null })}
      />
    </>
  );
};

export default CircularsManagement;
